import { DESIGN_MODE_CUSTOM_CSS_SCRIPT_SELECTOR } from "@/constants";
import { REWARDS_WIDGET_NAMES } from "@/constants/widget-names";
import { growaveConfigurationToken } from "@/tokens";
import { isShadowRoot } from "@/utils/isShadowRoot/isShadowRoot";
import { TRANSLATION_NAMESPACES } from "@apps/rewards/constants/constants";
import RewardsAmountFormatter from "@apps/rewards/lib/RewardsAmountFormatter/RewardsAmountFormatter";
import RewardsCurrencyFormatter from "@apps/rewards/lib/RewardsCurrencyFormatter";
import RewardsProgramAccountQuery from "@apps/rewards/modules/ProgramAccount/query/RewardsProgramAccountQuery";
import RewardsStorefrontValuesService from "@apps/rewards/modules/StorefrontValues/service/RewardsStorefrontValuesService";
import RewardsVipTierProgressQuery from "@apps/rewards/modules/VipTiers/queries/RewardsVipTierProgressQuery";
import RewardsVipTiersQuery from "@apps/rewards/modules/VipTiers/queries/RewardsVipTiersQuery";
import RewardsVipTiersService from "@apps/rewards/modules/VipTiers/service/RewardsVipTiersService";
import RewardsDiResolverService from "@apps/rewards/services/RewardsDiResolverService/RewardsDIResolverService";
import CustomizationManager from "@lib/CustomizationManager/CustomizationManager";
import { AuthenticationModule } from "@modules/authentication/AuthenticationModule";
import { CommonModule } from "@modules/common_module/CommonModule";
import { DynamicImportModule } from "@modules/dynamic_import/DynamicImportModule";
import { I18nModule } from "@modules/i18n/I18nModule";
import { MoneyModule } from "@modules/money/MoneyModule";
import { injectable } from "tsyringe";

import SliderController from "@components/Slider/SliderController";
import type { LoggerInterface } from "@interfaces/LoggerInterface";
import type { WidgetInterface } from "@interfaces/WidgetInterface";

import RewardsVipTiersPageWidgetComponent from "./RewardsVipTiersPageWidget.svelte";
import type { RewardsVipTiersPageWidgetConfig } from "./RewardsVipTiersPageWidgetConfigSchema";

@injectable()
class RewardsVipTiersPageWidget implements WidgetInterface {
    constructor(
        private readonly config: RewardsVipTiersPageWidgetConfig,
        private readonly logger: LoggerInterface
    ) {
        this.logger.debug("RewardsVipTiersPageWidget is initialized");
    }

    async init(): Promise<this> {
        const widgetNamespace = TRANSLATION_NAMESPACES.vipTiersPageWidget;
        const pointsNamespace = TRANSLATION_NAMESPACES.commonCriticalPoints;

        const diResolverService = new RewardsDiResolverService();
        const {
            i18nModule,
            vipTiersService,
            storefrontValuesService,
            vipTiersQuery,
            vipTierProgressQuery,
            programAccountQuery,
            currencyFormatter,
            amountFormatter,
            dynamicImportModule,
            authenticationModule,
            commonModule,
            moneyModule,
            customizationManager,
            growaveConfiguration,
        } = diResolverService.resolveAll({
            i18nModule: I18nModule,
            vipTiersService: RewardsVipTiersService,
            storefrontValuesService: RewardsStorefrontValuesService,
            vipTiersQuery: RewardsVipTiersQuery,
            vipTierProgressQuery: RewardsVipTierProgressQuery,
            programAccountQuery: RewardsProgramAccountQuery,
            currencyFormatter: RewardsCurrencyFormatter,
            amountFormatter: RewardsAmountFormatter,
            dynamicImportModule: DynamicImportModule,
            authenticationModule: AuthenticationModule,
            commonModule: CommonModule,
            moneyModule: MoneyModule,
            customizationManager: CustomizationManager,
            growaveConfiguration: growaveConfigurationToken,
        });

        const {
            i18n,
            i18nApiLoaderFactory,
            i18nCriticalLoaderFactory,
            localizationInfoProvider,
        } = i18nModule.exports;
        i18n.registerLoader(
            widgetNamespace,
            i18nApiLoaderFactory.getLoader(widgetNamespace)
        );
        i18n.registerLoader(
            pointsNamespace,
            i18nCriticalLoaderFactory.getLoader(pointsNamespace)
        );

        const { authenticationInfoProvider } = authenticationModule.exports;
        const { urlsBuilder, designModeProvider } = commonModule.exports;
        const { htmlSanitizer } = moneyModule.exports;
        const { dynamicImportManager } = dynamicImportModule.exports;
        const sliderController = new SliderController();

        const element = this.config.target;
        const shadowRoot = isShadowRoot(
            this.config.isWidgetShadowRootEnabled,
            growaveConfiguration.isShadowRootDisabledGlobally
        )
            ? element.shadowRoot || element.attachShadow({ mode: "open" })
            : null;

        const customizationManagerConfig = {
            widgetName: REWARDS_WIDGET_NAMES.REWARDS_VIP_TIERS_PAGE_WIDGET,
            placeholder: element,
            shadowRoot,
        };

        customizationManager.injectStylesFromDesignMode({
            element,
            shadowRoot,
            scriptSelector: DESIGN_MODE_CUSTOM_CSS_SCRIPT_SELECTOR,
        });
        customizationManager.injectStylesFromCustomization(
            customizationManagerConfig
        );

        await customizationManager.beforeWidgetInject(
            customizationManagerConfig
        );

        new RewardsVipTiersPageWidgetComponent({
            target: shadowRoot || element,
            props: {
                vipTiersService,
                storefrontValuesService,
                vipTiersQuery,
                vipTierProgressQuery,
                programAccountQuery,
                i18n,
                currencyFormatter,
                amountFormatter,
                sliderController,
                urlsBuilder,
                sanitizer: htmlSanitizer,
                authenticationInfoProvider,
                localizationInfoProvider,
                dynamicImportManager,
                logger: this.logger,
                namespace: widgetNamespace,
                pointsNamespace,
                isDesignMode: designModeProvider.isDesignMode(),
                getSlidesPerPage: this.getSlidesPerPage,
                afterWidgetInject: () => {
                    customizationManager.afterWidgetInject(
                        customizationManagerConfig
                    );
                },
            },
        });

        return this;
    }

    public getSlidesPerPage = (): number => {
        let slidesPerPage = 1;

        if (window.innerWidth >= 1200) slidesPerPage = 3;
        if (window.innerWidth >= 768) slidesPerPage = 2;

        return slidesPerPage;
    };
}

export default RewardsVipTiersPageWidget;
